import React, { useState, useEffect } from "react";
import editimg from "../../assets/img/edit.svg";
import deleteimg from "../../assets/img/delete.svg";
import searchimg from "../../assets/img/search.svg";
import downloadimg from "../../assets/img/download.svg";
import plusimg from "../../assets/img/plus.svg";
import chevrondown from "../../assets/img/chevron-down.svg";
import threedots from "../../assets/img/three-dots-vertical.svg";
import chevronup from "../../assets/img/chevron-up.svg";
import eyeimg from "../../assets/img/eye.svg";
import { fetchApi } from "../../api";
import * as XLSX from "xlsx";

function RoleMaster() {
  const godrejStyles = {
    primary: "#810055",
    secondary: "#707070",
    font: "GB-BodyCopy",
  };

  const [roles, setRoles] = useState([]);
  const [userNameMap, setUserNameMap] = useState({});

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await fetchApi("/api/rolemaster/all");
        const roleArray = Array.isArray(data) ? data : data.roles || data.data || [];
        const formattedData = roleArray.map((item, index) => ({ srno: index + 1, ...item }));
        setRoles(formattedData);
      } catch (error) {
        console.error("Error fetching roles:", error);
        setRoles([]);
      }
    };
    const fetchUserNames = async () => {
      try {
        const data = await fetchApi("/api/usermaster/all");
        const userArray = Array.isArray(data) ? data : data.users || data.data || [];
        const map = {};
        userArray.forEach(u => {
          if (u.um_email && u.um_user_name) map[u.um_email.toLowerCase()] = u.um_user_name;
        });
        setUserNameMap(map);
      } catch (error) {
        console.error("Error fetching user names:", error);
      }
    };
    fetchRoles();
    fetchUserNames();
  }, []);

  const [formData, setFormData] = useState({
    rm_status: "Active",
    rm_role: "",
    rm_role_description: "",
    rm_session_access: {
      "Generate email signature": false,
      "Master": false,
      "Address Master": false,
      "Admin": false,
      "Role & Authorization": false,
      "User Master": false,
      "Audit Log": false,
    },
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editOriginal, setEditOriginal] = useState({});
  const [editFormSnapshot, setEditFormSnapshot] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [viewRole, setViewRole] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);

  // Column filter state
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterDraft, setFilterDraft] = useState({ type: "Contains", value: "" });

  const updateFilterLive = (colKey, draft) => {
    setFilterDraft(draft);
  };

  // Column resize state
  const [columnWidths, setColumnWidths] = useState({});
  const resizingCol = React.useRef(null);
  const startX = React.useRef(0);
  const startWidth = React.useRef(0);

  const handleMouseDown = (e, colKey) => {
    e.preventDefault();
    resizingCol.current = colKey;
    startX.current = e.clientX;
    const th = e.target.parentElement;
    startWidth.current = th.offsetWidth;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!resizingCol.current) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(50, startWidth.current + diff);
    setColumnWidths(prev => ({ ...prev, [resizingCol.current]: newWidth }));
  };

  const handleMouseUp = () => {
    resizingCol.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [hoveredCol, setHoveredCol] = useState(null);

  const handleSort = (colKey) => {
    setSortConfig(prev => ({
      key: colKey,
      direction: prev.key === colKey && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const columns = [
    { key: "rm_status", label: "Status" },
    { key: "rm_role", label: "Role Code" },
    { key: "rm_role_description", label: "Role Description" },
    { key: "rm_session_access", label: "Session Access" },
    { key: "rm_inserted_by", label: "Inserted By" },
    { key: "rm_inserted_on", label: "Inserted On" },
    { key: "rm_modified_by", label: "Modified By" },
    { key: "rm_modified_on", label: "Modified On" },
  ];

  const applyColumnFilter = (colKey) => {
    if (filterDraft.value || filterDraft.type === "Blank" || filterDraft.type === "Not Blank") {
      setColumnFilters(prev => ({ ...prev, [colKey]: { ...filterDraft } }));
    }
    setActiveFilter(null);
    setCurrentPage(0);
  };

  const resetColumnFilter = (colKey) => {
    setColumnFilters(prev => { const n = { ...prev }; delete n[colKey]; return n; });
    setFilterDraft({ type: "Contains", value: "" });
    setActiveFilter(null);
    setCurrentPage(0);
  };

  const matchesFilter = (val, filter) => {
    const cellVal = String(val || "").toLowerCase();
    const filterVal = (filter.value || "").toLowerCase();
    switch (filter.type) {
      case "Contains": return cellVal.includes(filterVal);
      case "Not Contains": return !cellVal.includes(filterVal);
      case "Equals": return cellVal === filterVal;
      case "Not Equals": return cellVal !== filterVal;
      case "Starts With": return cellVal.startsWith(filterVal);
      case "Ends With": return cellVal.endsWith(filterVal);
      case "Blank": return cellVal.trim() === "";
      case "Not Blank": return cellVal.trim() !== "";
      default: return true;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  const resolveUserName = (value) => {
    if (!value) return "";
    if (value.includes("@")) return userNameMap[value.toLowerCase()] || value;
    return value;
  };

  const getCellValue = (role, key) => {
    if (key === "rm_inserted_on" || key === "rm_modified_on") {
      return formatDateTime(role[key]);
    }
    if (key === "rm_inserted_by" || key === "rm_modified_by") {
      return resolveUserName(role[key]);
    }
    if (key === "rm_session_access") {
      return Array.isArray(role.rm_session_access) ? role.rm_session_access.join(', ') : (typeof role.rm_session_access === 'object' && role.rm_session_access ? Object.keys(role.rm_session_access).filter(k => role.rm_session_access[k]).join(', ') : String(role.rm_session_access || ''));
    }
    return role[key] || "";
  };

  const filteredRoles = roles.filter(role => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matches = columns.some(col => String(getCellValue(role, col.key) || "").toLowerCase().includes(term));
      if (!matches) return false;
    }
    for (const [colKey, filter] of Object.entries(columnFilters)) {
      if (!matchesFilter(getCellValue(role, colKey), filter)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (!sortConfig.key) {
      if (a.rm_status !== b.rm_status) {
        return a.rm_status === 'Active' ? -1 : 1;
      }
      const aTime = new Date(a.rm_modified_on || a.rm_inserted_on || 0).getTime();
      const bTime = new Date(b.rm_modified_on || b.rm_inserted_on || 0).getTime();
      return bTime - aTime;
    }
    const aVal = String(getCellValue(a, sortConfig.key) || "").toLowerCase();
    const bVal = String(getCellValue(b, sortConfig.key) || "").toLowerCase();
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const numberOfPages = () => Math.ceil(filteredRoles.length / pageSize);

  const handleSessionAccessChange = (sessionName) => {
    setFormData(prev => {
      const newAccess = {
        ...prev.rm_session_access,
        [sessionName]: !prev.rm_session_access[sessionName]
      };
      // Auto-set parent titles based on children
      newAccess["Master"] = newAccess["Address Master"] === true;
      newAccess["Admin"] = newAccess["Role & Authorization"] === true || newAccess["User Master"] === true || newAccess["Audit Log"] === true;
      return { ...prev, rm_session_access: newAccess };
    });
  };

  const sessionStructure = [
    { name: "Generate email signature", level: 0 },
    { name: "Master", level: 0, isTitle: true },
    { name: "Address Master", level: 1 },
    { name: "Admin", level: 0, isTitle: true },
    { name: "Role & Authorization", level: 1 },
    { name: "User Master", level: 1 },
    { name: "Audit Log", level: 1 },
  ];

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    if (id === 'rm_role') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 20);
      setFormData(prev => ({ ...prev, [id]: numericValue }));
    } else if (id === 'rm_role_description') {
      const alphaValue = value.replace(/[^a-zA-Z ]/g, '').slice(0, 100);
      setFormData(prev => ({ ...prev, [id]: alphaValue }));
    } else {
      setFormData(prev => ({ ...prev, [id]: value }));
    }
  };

  const loggedInUser = sessionStorage.getItem("userDisplayName") || sessionStorage.getItem("userEmail") || "";

  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!formData.rm_role || formData.rm_role.trim() === '') {
      errors.rm_role = 'Role Code is mandatory';
    }
    if (!formData.rm_role_description || formData.rm_role_description.trim() === '') {
      errors.rm_role_description = 'Role Description is mandatory';
    }
    const hasSessionAccess = Object.values(formData.rm_session_access).some(v => v === true);
    if (!hasSessionAccess) {
      errors.rm_session_access = 'At least one session access must be selected';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    try {
      // Check for duplicate role code
      if (!isEditing) {
        const duplicate = roles.find(r => String(r.rm_role).trim() === String(formData.rm_role).trim());
        if (duplicate) {
          setRoleCodeExistsError(true);
          return;
        }
      }
      const now = new Date().toISOString();
      let payload = { ...formData };

      // Only save checked session names (filter out false values)
      if (payload.rm_session_access && typeof payload.rm_session_access === 'object') {
        const checkedSessions = Object.keys(payload.rm_session_access).filter(key => payload.rm_session_access[key]);
        payload.rm_session_access = checkedSessions;
      }

      if (isEditing) {
        payload.rm_id = editId;
        payload.rm_inserted_by = editOriginal.rm_inserted_by;
        payload.rm_inserted_on = editOriginal.rm_inserted_on;
        payload.rm_modified_by = loggedInUser;
        payload.rm_modified_on = now;
      } else {
        payload.rm_inserted_by = loggedInUser;
        payload.rm_inserted_on = now;
        payload.rm_modified_by = loggedInUser;
        payload.rm_modified_on = now;
      }

      if (isEditing) {
        await fetchApi("/api/rolemaster/update", {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await fetchApi("/api/rolemaster/save", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      // Re-fetch to get latest data from DB
      const data = await fetchApi("/api/rolemaster/all");
      const roleArray = Array.isArray(data) ? data : data.roles || data.data || [];
      setRoles(roleArray.map((item, index) => ({ srno: index + 1, ...item })));

      const msg = isEditing ? "Role Edited Successfully" : "Role Added Successfully";
      resetForm();
      setShowForm(false);
      setSuccessMessage(msg);
    } catch (error) {
      console.error("Error saving role:", error);
      alert("Failed to save role. Please try again.");
    }
  };

  const resetForm = () => {
    setFormData({ 
      rm_status: "Active",
      rm_role: "",
      rm_role_description: "",
      rm_session_access: {
        "Generate email signature": false,
        "Master": false,
        "Address Master": false,
        "Admin": false,
        "Role & Authorization": false,
        "User Master": false,
        "Audit Log": false,
      },
    });
    setIsEditing(false);
    setEditId(null);
    setViewId(null);
  };

  const handleEdit = (role) => {
    // Convert array format from DB back to checkbox object format
    let sessionAccess = {
      "Generate email signature": false,
      "Master": false,
      "Address Master": false,
      "Admin": false,
      "Role & Authorization": false,
      "User Master": false,
      "Audit Log": false,
    };
    if (Array.isArray(role.rm_session_access)) {
      role.rm_session_access.forEach(name => { if (sessionAccess.hasOwnProperty(name)) sessionAccess[name] = true; });
    } else if (role.rm_session_access && typeof role.rm_session_access === 'object') {
      sessionAccess = { ...sessionAccess, ...role.rm_session_access };
    }
    setFormData({
      rm_status: role.rm_status,
      rm_role: role.rm_role,
      rm_role_description: role.rm_role_description,
      rm_session_access: sessionAccess,
    });
    setEditOriginal({ rm_inserted_by: role.rm_inserted_by, rm_inserted_on: role.rm_inserted_on });
    setEditFormSnapshot({
      rm_status: role.rm_status,
      rm_role: role.rm_role,
      rm_role_description: role.rm_role_description,
      rm_session_access: sessionAccess,
    });
    setIsEditing(true);
    setEditId(role.rm_id);
    setShowForm(true);
  };

  // GS-SEC-006: Sanitize cell values to prevent CSV formula injection
  const sanitizeForCSV = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (/^[=+\-@\t\r|]/.test(str)) return "'" + str;
    return str;
  };

  const handleDownload = () => {
    const exportData = filteredRoles.map(role => ({
      "Status": sanitizeForCSV(role.rm_status),
      "Role": sanitizeForCSV(role.rm_role),
      "Role Description": sanitizeForCSV(role.rm_role_description),
      "Session Access": sanitizeForCSV(Array.isArray(role.rm_session_access) ? role.rm_session_access.join(', ') : (typeof role.rm_session_access === 'object' ? Object.keys(role.rm_session_access).filter(k => role.rm_session_access[k]).join(', ') : role.rm_session_access)),
      "Inserted By": sanitizeForCSV(role.rm_inserted_by),
      "Inserted On": sanitizeForCSV(formatDateTime(role.rm_inserted_on)),
      "Modified By": sanitizeForCSV(role.rm_modified_by),
      "Modified On": sanitizeForCSV(formatDateTime(role.rm_modified_on)),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RoleMaster");
    XLSX.writeFile(wb, "RoleMasterData.xlsx");
  };

  const [deleteError, setDeleteError] = useState(null);
  const [roleCodeExistsError, setRoleCodeExistsError] = useState(false);
  const [noChangesError, setNoChangesError] = useState(false);

  const handleDelete = async (role) => {
    // Check if role is in use by any user
    try {
      const data = await fetchApi("/api/usermaster/all");
      const userArray = Array.isArray(data) ? data : data.users || data.data || [];
      const roleCode = String(role.rm_role || '').trim();
      const inUse = userArray.some(u => String(u.um_role_name || '').trim() === roleCode);
      if (inUse) {
        setDeleteError("This role is currently in use and cannot be deleted. Please make it inactive from the Edit option.");
        return;
      }
    } catch (error) {
      console.error("Error checking role usage:", error);
    }

    if (!window.confirm("Are you sure you want to delete this role?")) return;
    try {
      await fetchApi(`/api/rolemaster/${role.rm_role}`, { method: "DELETE" });
      setRoles(prev => prev.filter(r => r.rm_role !== role.rm_role));
      setSuccessMessage("Role Deleted Successfully.");
    } catch (error) {
      console.error("Error deleting role:", error);
      alert("Failed to delete role. Please try again.");
    }
  };

  const btnStyle = {
    backgroundColor: godrejStyles.primary,
    color: "white",
    border: "none",
    padding: "3px 15px",
    borderRadius: "25px",
  };

  return (
    <div style={{ fontFamily: godrejStyles.font, paddingRight: "10px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "10px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <h1 style={{ fontSize: "18px", fontWeight: "400", color: "#333", margin: "0", marginRight: "15px" }}>Role Master</h1>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="btn" style={{...btnStyle, textTransform: 'none', fontSize: '14px', fontWeight: '400'}}>
            <img src={plusimg} alt="Add" style={{ width: '16px', height: '21px', marginRight: '8px', filter: 'brightness(0) invert(1)' }} />
            Add New Role
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input 
            type="text" 
            placeholder="Search here..." 
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
            style={{ 
              width: "400px", 
              height: "35px", 
              padding: "4px 34px", 
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
              backgroundImage: `url(${searchimg})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "10px center",
              backgroundSize: "16px 16px"
            }} 
          />
          <img 
            src={downloadimg} 
            alt="Download" 
            title="Download" 
            onClick={handleDownload}
            style={{ 
              width: "20px", 
              height: "20px", 
              marginLeft: "15px",
              cursor: "pointer"
            }} 
          />
        </div>
      </div>

      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '0',
            borderRadius: '0',
            width: '70%',
            maxWidth: '800px',
            maxHeight: '80%',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #ccc', padding: '20px 20px 15px 20px' }}>
              <h2 style={{ color: '#333', fontSize: '18px', fontWeight: '500', margin: 0 }}>
                {isEditing ? 'Edit Role' : 'Add New Role'}
              </h2>
              <button 
                onClick={() => { resetForm(); setShowForm(false); }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#707070',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '0' }}>
              <form style={{ padding: '0', margin: '0' }}>
                <div style={{ width: '100%', padding: '20px', marginTop: '10px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', margin: 0, padding: 0 }}>
                    <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '20px' }}>
                      <label className="form-label" style={{ color: '#707070', marginBottom: '8px', display: 'block' }}>Status</label>
                      <div className="sw-status col">
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '3px' }}>
                          <div className="switch">
                            <input 
                              className="react-switch-checkbox" 
                              id="react-switch-new-undefined" 
                              type="checkbox" 
                              checked={formData.rm_status === "Active"}
                              onChange={(e) => setFormData(prev => ({ ...prev, rm_status: e.target.checked ? "Active" : "Inactive" }))}
                              style={{ display: 'none' }}
                            />
                            <label 
                              className="react-switch-label" 
                              htmlFor="react-switch-new-undefined"
                              style={{
                                width: '29px',
                                height: '13px',
                                backgroundColor: formData.rm_status === "Active" ? '#33C037' : '#707070',
                                borderRadius: '13px',
                                position: 'relative',
                                cursor: 'pointer',
                                display: 'inline-block',
                                transition: 'background-color 0.3s',
                                marginTop: '8px'
                              }}
                            >
                              <span 
                                className="react-switch-button undefined"
                                style={{
                                  position: 'absolute',
                                  top: '-1px',
                                  left: formData.rm_status === "Active" ? '15px' : '-1px',
                                  width: '15px',
                                  height: '15px',
                                  backgroundColor: '#fff',
                                  border: `1px solid ${formData.rm_status === "Active" ? '#33C037' : '#707070'}`,
                                  borderRadius: '50%',
                                  transition: 'left 0.3s, border-color 0.3s'
                                }}
                              ></span>
                            </label>
                          </div>
                          <label className={`fieldLabel ${formData.rm_status} form-label`} style={{ marginLeft: '10px', color: '#707070' }}>{formData.rm_status}</label>
                        </div>
                      </div>
                    </div>
                    {[
                      ["rm_role", "Role Code"],
                      ["rm_role_description", "Role Description"],
                    ].map(([id, label]) => {
                      const isReadOnly = isEditing && id === 'rm_role';
                      return (
                      <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '20px' }} key={id}>
                        <input 
                          className="form-control" 
                          id={id} 
                          type="text" 
                          placeholder={label}
                          value={formData[id]} 
                          onChange={handleInputChange}
                          readOnly={isReadOnly}
                          style={{
                            border: `1px solid ${validationErrors[id] ? '#dc3545' : '#ccc'}`,
                            borderRadius: '4px',
                            padding: '15px 18px',
                            fontSize: '16px',
                            width: '100%',
                            height: '50px',
                            backgroundColor: isReadOnly ? '#f5f5f5' : '#fff'
                          }}
                        />
                        {validationErrors[id] && (
                          <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                            {validationErrors[id]}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  
                  <div style={{ width: '100%', padding: '0 10px', paddingBottom: '20px' }}>
                    <label className="form-label" style={{ color: '#707070', marginBottom: '8px', display: 'block' }}>Session Access (Mandatory)</label>
                    <div style={{ border: `1px solid ${validationErrors.rm_session_access ? '#dc3545' : '#ccc'}`, borderRadius: '4px', padding: '15px', backgroundColor: '#f9f9f9' }}>
                      <div style={{ display: 'flex', marginBottom: '10px', fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
                        <div style={{ width: '70%', paddingLeft: '10px' }}>Session</div>
                        <div style={{ width: '30%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                          <span>Access</span>
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFormData(prev => ({
                                ...prev,
                                rm_session_access: {
                                  "Generate email signature": isChecked,
                                  "Master": isChecked,
                                  "Address Master": isChecked,
                                  "Admin": isChecked,
                                  "Role & Authorization": isChecked,
                                  "User Master": isChecked,
                                  "Audit Log": isChecked,
                                }
                              }));
                            }}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '12px', color: '#666' }}>Select All</span>
                        </div>
                      </div>
                      {sessionStructure.map((session) => (
                        <div key={session.name} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: '8px',
                          backgroundColor: session.level === 0 ? '#f0f0f0' : 'transparent',
                          padding: '4px 0',
                          borderRadius: '4px'
                        }}>
                          <div style={{ width: '70%', paddingLeft: (10 + session.level * 20) + 'px', fontSize: '14px', fontWeight: session.level === 0 && session.isTitle ? 'bold' : 'normal' }}>
                            {session.name}
                          </div>
                          <div style={{ width: '30%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {!session.isTitle && (
                              <input
                                type="checkbox"
                                checked={formData.rm_session_access[session.name] || false}
                                onChange={() => handleSessionAccessChange(session.name)}
                                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {validationErrors.rm_session_access && (
                      <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                        {validationErrors.rm_session_access}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', width: '100%' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button type="button" className="btn" onClick={() => { resetForm(); setShowForm(false); }} style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>Cancel</button>
                      <button type="button" className="btn" onClick={() => { if (isEditing && editFormSnapshot) { setFormData(editFormSnapshot); } else { setFormData({ rm_status: "Active", rm_role: "", rm_role_description: "", rm_session_access: { "Generate email signature": false, "Master": false, "Address Master": false, "Admin": false, "Role & Authorization": false, "User Master": false, "Audit Log": false } }); } setValidationErrors({}); }} style={{ backgroundColor: 'transparent', color: '#810055', border: 'none', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px' }}>Reset Fields</button>
                    </div>
                    <div>
                      <button type="button" className="btn me-2" onClick={() => { if (!validateForm()) return; if (isEditing) { if (JSON.stringify(formData) === JSON.stringify(editFormSnapshot)) { setNoChangesError(true); return; } setShowConfirmUpdate(true); } else { handleSave(); } }} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>{isEditing ? "Update" : "Add"}</button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* No Changes Popup */}
      {noChangesError && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1003
        }}>
          <div style={{
            backgroundColor: 'white', padding: '30px 40px', borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)', textAlign: 'center', minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>No Changes Found.</p>
            <button type="button" className="btn" onClick={() => setNoChangesError(false)} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>OK</button>
          </div>
        </div>
      )}

      {/* Role Code Exists Popup */}
      {roleCodeExistsError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1003
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>Role Code already Exists.</p>
            <button type="button" className="btn" onClick={() => setRoleCodeExistsError(false)} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>OK</button>
          </div>
        </div>
      )}

      {/* Confirm Update Popup */}
      {showConfirmUpdate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1003
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>Are you sure, do you want to update Role ?</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
              <button 
                type="button" 
                className="btn" 
                onClick={() => setShowConfirmUpdate(false)} 
                style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}
              >
                No
              </button>
              <button 
                type="button" 
                className="btn" 
                onClick={() => { setShowConfirmUpdate(false); handleSave(); }} 
                style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message Popup */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>{successMessage}</p>
            <button 
              type="button" 
              className="btn" 
              onClick={() => setSuccessMessage(null)} 
              style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Delete Error Popup */}
      {deleteError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>{deleteError}</p>
            <button 
              type="button" 
              className="btn" 
              onClick={() => setDeleteError(null)} 
              style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* View Role Popup */}
      {viewRole && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '0',
            borderRadius: '0',
            width: '70%',
            maxWidth: '800px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #ccc', padding: '20px 20px 15px 20px' }}>
              <h2 style={{ color: '#333', fontSize: '18px', fontWeight: '500', margin: 0 }}>View Role</h2>
              <button 
                onClick={() => setViewRole(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#707070',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ width: '100%', padding: '15px 20px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', margin: 0, padding: 0 }}>
                {[
                  ["Status", viewRole.rm_status],
                  ["Role Code", viewRole.rm_role],
                  ["Role Description", viewRole.rm_role_description],
                  ["Session Access", getCellValue(viewRole, "rm_session_access")],
                  ["Inserted By", resolveUserName(viewRole.rm_inserted_by)],
                  ["Inserted On", formatDateTime(viewRole.rm_inserted_on)],
                  ["Modified By", resolveUserName(viewRole.rm_modified_by)],
                  ["Modified On", formatDateTime(viewRole.rm_modified_on)],
                ].map(([label, value]) => (
                  <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '12px' }} key={label}>
                    <label style={{ color: '#707070', marginBottom: '2px', display: 'block', fontSize: '12px' }}>{label}</label>
                    <div style={{
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      width: '100%',
                      height: '38px',
                      backgroundColor: '#f9f9f9',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden'
                    }}
                      title={value || '-'}
                    >
                      <span style={{ whiteSpace: 'nowrap', overflow: 'auto', width: '100%', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>{value || '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button type="button" className="btn" onClick={() => setViewRole(null)} style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        {Object.keys(columnFilters).length > 0 && (
          <div className="filterList" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: '5px 10px' }}>
            {Object.entries(columnFilters).map(([colKey, filter]) => {
              const col = columns.find(c => c.key === colKey);
              return (
                <div key={colKey} className="filter-col pr-0 me-2 mb-2" style={{ display: 'flex', alignItems: 'center', backgroundColor: '#F9F2F6', borderRadius: '25px', padding: '2px 8px', fontSize: '13px' }}>
                  <span className="ps-2 btn-text">{col?.label || colKey} : {filter.type === 'Blank' || filter.type === 'Not Blank' ? filter.type : filter.value}</span>
                  <div onClick={() => resetColumnFilter(colKey)} style={{ cursor: 'pointer', marginLeft: '4px' }}>
                    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 16 16" height="25" width="25" xmlns="http://www.w3.org/2000/svg"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"></path></svg>
                  </div>
                </div>
              );
            })}
            <div>
              <button type="button" className="btn" style={{ backgroundColor: 'white', color: '#810055', border: '1px solid #810055', padding: '3px 15px', borderRadius: '25px', textTransform: 'none', fontSize: '14px', fontWeight: '500', marginLeft: '6px' }} onClick={() => { setColumnFilters({}); setCurrentPage(0); }}>Clear All</button>
            </div>
          </div>
        )}
        <div style={{ maxHeight: "400px", overflowY: "scroll", overflowX: "scroll", padding: "0px 0px 10px 10px", boxSizing: "border-box", backgroundColor: "#fff", marginTop: "5px" }}>
          <table className="table table-hover mb-0" style={{ width: "100%", minWidth: "1400px", borderCollapse: "separate", borderSpacing: "0 3px" }}>
            <thead style={{ backgroundColor: godrejStyles.secondary, color: "#fff", fontSize: "0.9rem", position: "sticky", top: 0, zIndex: 2, verticalAlign: "top", height: "36px", fontWeight: "400", whiteSpace: "nowrap" }}>
              <tr>
                {columns.map(col => (
                  <th key={col.key} onMouseEnter={() => setHoveredCol(col.key)} onMouseLeave={() => setHoveredCol(null)} style={{ height: "36px", padding: "8px 10px", fontWeight: "400", whiteSpace: "nowrap", position: "relative", textAlign: "left", width: columnWidths[col.key] ? `${columnWidths[col.key]}px` : "auto", backgroundColor: godrejStyles.secondary }}>
                    <span style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                      {col.label}
                      <img
                        src={chevronup}
                        alt="sort"
                        onClick={() => handleSort(col.key)}
                        style={{ width: "12px", height: "12px", marginLeft: "5px", filter: "brightness(0) invert(1)", cursor: "pointer", transform: sortConfig.key === col.key && sortConfig.direction === "desc" ? "rotate(180deg)" : "none", opacity: hoveredCol === col.key || sortConfig.key === col.key ? 1 : 0, transition: "opacity 0.2s" }}
                      />
                      <img
                        src={threedots}
                        alt="filter"
                        onClick={() => {
                          if (activeFilter === col.key) {
                            setActiveFilter(null);
                          } else {
                            setActiveFilter(col.key);
                            setFilterDraft(columnFilters[col.key] || { type: "Contains", value: "" });
                          }
                        }}
                        style={{ width: "14px", height: "14px", marginLeft: "5px", filter: "brightness(0) invert(1)", cursor: "pointer", opacity: hoveredCol === col.key || activeFilter === col.key || columnFilters[col.key] ? 1 : 0, transition: "opacity 0.2s" }}
                      />
                    </span>
                    {activeFilter === col.key && (
                      <div style={{ position: "absolute", top: "36px", left: "0", zIndex: 1000, background: "#fff", border: "1px solid #ccc", borderRadius: "4px", padding: "12px", minWidth: "200px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                        <select
                          className="form-control"
                          value={filterDraft.type}
                          onChange={(e) => updateFilterLive(col.key, { ...filterDraft, type: e.target.value })}
                          style={{ fontSize: "13px", marginBottom: "8px", appearance: "none", WebkitAppearance: "none", MozAppearance: "none", backgroundImage: `url(${chevrondown})`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", backgroundSize: "12px 12px", paddingRight: "30px" }}
                        >
                          {["Contains", "Not Contains", "Equals", "Not Equals", "Starts With", "Ends With", "Blank", "Not Blank"].map(opt => (
                            <option key={opt}>{opt}</option>
                          ))}
                        </select>
                        {!["Blank", "Not Blank"].includes(filterDraft.type) && (
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Filter..."
                            value={filterDraft.value}
                            onChange={(e) => updateFilterLive(col.key, { ...filterDraft, value: e.target.value })}
                            style={{ fontSize: "13px", marginBottom: "8px" }}
                          />
                        )}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                          <span onClick={() => resetColumnFilter(col.key)} style={{ cursor: "pointer", color: "#707070", fontSize: "12px", fontWeight: "600" }}>RESET</span>
                          <span onClick={() => applyColumnFilter(col.key)} style={{ cursor: "pointer", color: "#810055", fontSize: "12px", fontWeight: "600" }}>APPLY</span>
                        </div>
                      </div>
                    )}
                    <div onMouseDown={(e) => handleMouseDown(e, col.key)} style={{ position: "absolute", right: 0, top: "25%", height: "50%", width: "2px", backgroundColor: "#a0a0a0", cursor: "col-resize" }}></div>
                  </th>
                ))}
                <th style={{ height: "36px", padding: "8px 10px", fontWeight: "400", whiteSpace: "nowrap", textAlign: "left", backgroundColor: godrejStyles.secondary, position: "sticky", right: 0, zIndex: 3 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoles.length > 0 ? (
                (viewId ? filteredRoles.filter(r => r.rm_id === viewId) : filteredRoles.slice(currentPage * pageSize, (currentPage + 1) * pageSize))
                  .map(role => (
                    <tr key={role.rm_id} style={{ backgroundColor: "#fff" }}>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", borderLeft: "1px solid #e0e0e0" }}>
                        <div style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "6px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          backgroundColor: role.rm_status?.toLowerCase() === 'active' ? 'rgba(25, 135, 84, 0.1)' : 'rgba(112, 112, 112, 0.1)'
                        }}>
                          <img 
                            src={role.rm_status?.toLowerCase() === 'active' ? require('../../assets/img/check-circle-fill.svg').default : require('../../assets/img/x-circle-fill.svg').default}
                            alt={role.rm_status}
                            style={{
                              width: "16px",
                              height: "16px",
                              filter: role.rm_status?.toLowerCase() === 'active' 
                                ? 'brightness(0) saturate(100%) invert(35%) sepia(85%) saturate(1352%) hue-rotate(87deg) brightness(104%) contrast(97%)'
                                : 'brightness(0) saturate(100%) invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)'
                            }}
                          />
                          <span style={{
                            color: role.rm_status?.toLowerCase() === 'active' ? '#198754' : '#707070',
                            fontWeight: "500",
                            fontSize: "13px"
                          }}>
                            {role.rm_status}
                          </span>
                        </div>
                      </td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{role.rm_role}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{role.rm_role_description}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{getCellValue(role, "rm_session_access")}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{role.rm_inserted_by}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{formatDateTime(role.rm_inserted_on)}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{role.rm_modified_by}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{formatDateTime(role.rm_modified_on)}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", borderRight: "1px solid #e0e0e0", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}>
                        <button onClick={() => setViewRole(role)} className="btn btn-sm me-1" style={{ background: "none", border: "none", padding: 0, boxShadow: "none" }}>
                          <img src={eyeimg} alt="View" title="View" style={{ width: "20px", height: "20px", cursor: "pointer", filter: "invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)" }} />
                        </button>
                        <button onClick={() => handleEdit(role)} className="btn btn-sm me-1" style={{ background: "none", border: "none", padding: 0, boxShadow: "none" }}>
                          <img src={editimg} alt="Edit" title="Edit" style={{ width: "20px", height: "20px", cursor: "pointer" }} />
                        </button>
                        <button onClick={() => handleDelete(role)} className="btn btn-sm" style={{ background: "none", border: "none", padding: 0, boxShadow: "none" }}>
                          <img src={deleteimg} alt="Delete" title="Delete" style={{ width: "20px", height: "20px", cursor: "pointer", filter: "invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)" }} />
                        </button>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4" style={{ color: godrejStyles.secondary }}>{searchTerm ? "No matching records found" : "No record found"}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '20px', backgroundColor: '#fff' }}>
          <div style={{ padding: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '14px', color: '#707070' }}>Items per page</span>
              <input 
                min="1" 
                max="10"
                type="number" 
                value={pageSize}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val >= 1 && val <= 10) {
                    setPageSize(val);
                    setCurrentPage(0);
                  }
                }}
                style={{
                  width: '60px',
                  height: '30px',
                  border: 'none',
                  borderBottom: '1px solid #000',
                  borderRadius: '0',
                  textAlign: 'center',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <button 
                type="button" 
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0 || filteredRoles.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'transparent',
                  color: (currentPage === 0 || filteredRoles.length === 0) ? '#ccc' : '#707070',
                  cursor: (currentPage === 0 || filteredRoles.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                  <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"></path>
                </svg>
                Previous
              </button>
              <div style={{ width: '1px', height: '30px', backgroundColor: '#e7e7e7' }}></div>
              <button 
                type="button" 
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= numberOfPages() - 1 || filteredRoles.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'transparent',
                  color: (currentPage >= numberOfPages() - 1 || filteredRoles.length === 0) ? '#ccc' : '#810055',
                  cursor: (currentPage >= numberOfPages() - 1 || filteredRoles.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Next
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                  <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"></path>
                </svg>
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '14px', color: '#707070' }}>You are on Page No.</span>
                <select 
                  value={filteredRoles.length > 0 ? currentPage + 1 : 1}
                  onChange={(e) => setCurrentPage(parseInt(e.target.value) - 1)}
                  disabled={filteredRoles.length === 0}
                  style={{
                    padding: '5px 10px',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    minWidth: '60px',
                    backgroundColor: 'transparent',
                    outline: 'none'
                  }}
                >
                  {filteredRoles.length > 0 ? (
                    Array.from({ length: numberOfPages() }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))
                  ) : (
                    <option value={1}>1</option>
                  )}
                </select>
              </div>
              <span style={{ fontSize: '14px', color: '#707070' }}>Total {filteredRoles.length} Records</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleMaster;